import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT, OrganizationTypes, RAW_DATE_FORMAT } from '@/common/constants';
import { ValuesMap } from '@/common/types';
import { API_URL, IEmployerCalendarEventDtoCategoryEnum, IInvoiceStatusType } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';

import { EmployerCalendarPayload } from '../EmployerCalendar.types';

interface Params {
  currentType?: IEmployerCalendarEventDtoCategoryEnum | null;
  startDate: string;
  endDate: string;
}

const QUERY_KEY = 'GET_EMPLOYER_CALENDAR_EVENTS';
const useGetEmployerEventsQuery = ({ startDate, endDate, currentType }: Params) => {
  const { observingOrganization } = useCurrentOrganization();

  const { data, isLoading, isSuccess, isError } = useQuery(
    [QUERY_KEY, observingOrganization?.id, observingOrganization?.type, startDate, endDate],
    () =>
      api.get(API_URL.METRICS_EMPLOYER_CALENDAR_EVENTS, {
        params: {
          from_date: startDate,
          to_date: endDate,
        },
      }),
    {
      retry: 0,
      enabled:
        observingOrganization?.type === OrganizationTypes.employer ||
        observingOrganization?.type === OrganizationTypes.company ||
        observingOrganization?.type === OrganizationTypes.subsidiary,
    }
  );

  const getTime = (eventTime?: string | Date, eventTimestamp?: Date) => {
    let eventDate = eventTime as unknown as string;
    if (eventTimestamp) {
      return dayjs(eventTimestamp);
    }
    if (eventDate) {
      if (eventDate?.length === RAW_DATE_FORMAT.length) {
        return dayjs(eventDate, RAW_DATE_FORMAT).endOf('day').add(-1, 'hour');
      } else {
        return dayjs(eventDate);
      }
    }
  };
  const formattedItems = useMemo(() => {
    const list = data?.data?.events;

    let formattedList: EmployerCalendarPayload[] =
      list?.map((item, index) => {
        const mappedItem: EmployerCalendarPayload = {
          id: `index_${index}`,
          category: item.category,
          invoiceId: `${item.invoice_id || ''}`,

          name: item.name,
          plans: item.plans,

          type: item.type,
          invoiceStatus: item.invoice_status as IInvoiceStatusType,
          eventDate: getTime(item.event_date),
        };
        return mappedItem;
      }) || [];
    if (currentType) {
      formattedList = formattedList.filter((item) => item.category === currentType);
    }
    return formattedList;
  }, [currentType, data?.data?.events]);

  const collapsedDataMap = useMemo<ValuesMap<EmployerCalendarPayload[]>>(() => {
    const currentMap: ValuesMap<EmployerCalendarPayload[]> = {};

    formattedItems.forEach((item) => {
      if (item.eventDate) {
        const key = item.eventDate.format(DEFAULT_DATE_FORMAT);
        if (currentMap[key]) {
          currentMap[key] = [...currentMap[key], item];
        } else {
          currentMap[key] = [item];
        }
      }
    });

    return currentMap;
  }, [formattedItems]);
  return {
    dataMap: collapsedDataMap,
    data: formattedItems,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useGetEmployerEventsQuery;
